import { CSSProperties, useLayoutEffect, useRef } from "react";
import { MotionValue } from "framer-motion";
import SceneTrack, { useSceneRender } from "../SceneTrack";
import SceneCanvas from "../SceneCanvas";
import { box, clamp, easeIn, easeOut, lerp, range } from "../morph";
import { EXPERIENCES } from "../../../data/experience";
import { BASE, SCHEDULE, SPAN, firstLabel, labelFor, lastLabel, monthAt } from "./experienceSchedule";
import "./ExperienceScene.css";

/*
 * 03 · Experience — "Fold & Branch", portrait.
 *
 * The desktop scene turned on its side for a phone held upright: the time
 * axis runs down the left edge, each role's lane forks off to the right, and
 * its card unfolds downward from a hinge on its top edge. Same schedule as the
 * desktop scene (experienceSchedule), so it tells the story to the same beat.
 */

/** Portrait design space. */
const W = 390;
const H = 844;
const NAV = 64;

const AX_X = 34;
const LANE_X = 58;
const Y0 = 266;
const Y1 = 812;
const MH = (Y1 - Y0) / (SPAN + 1);
const yAt = (m: number) => Y0 + MH * (m + 0.5);

const CARD_X = 76;
const CARD_W = W - CARD_X - 16;
const HEAD_H = 72;

const JOBS = SCHEDULE.map((beat) => {
    const ys = yAt(beat.m0);
    const ye = yAt(beat.m1);
    return { ...beat, ys, ye, headY: ys - MH / 2 };
});

/** Tick boundaries: the start, every January, and the end. */
const TICKS = Array.from({ length: SPAN + 2 }, (_, i) => ({
    y: Y0 + i * MH,
    major: i === 0 || i === SPAN + 1 || (BASE + i) % 12 === 0,
    year: (BASE + i) % 12 === 0 && i > 0 && i <= SPAN ? Math.floor((BASE + i) / 12) : null,
}));

export default function ExperiencePhoneScene() {
    return (
        <SceneTrack pin={300} tint="150, 220, 120">
            {(p) => <PhoneCanvas p={p} />}
        </SceneTrack>
    );
}

function PhoneCanvas({ p }: { p: MotionValue<number> }) {
    const root = useRef<HTMLDivElement>(null);
    const els = useRef<{
        prog: SVGPathElement;
        ticks: SVGLineElement[];
        ph: SVGLineElement;
        phDot: SVGCircleElement;
        digits: HTMLElement[];
        jobs: {
            fork: SVGPathElement;
            lane: SVGPathElement;
            merge: SVGPathElement;
            head: HTMLElement;
            body: HTMLElement;
            shades: HTMLElement[];
            items: HTMLElement[];
        }[];
    } | null>(null);

    useLayoutEffect(() => {
        const r = root.current;
        if (!r) return;
        const one = <T extends Element>(s: string) => r.querySelector(s) as T;
        const all = <T extends Element>(s: string, from: ParentNode = r) => Array.from(from.querySelectorAll(s)) as T[];
        els.current = {
            prog: one<SVGPathElement>(".ex-prog"),
            ticks: all<SVGLineElement>(".ex-tick"),
            ph: one<SVGLineElement>(".ex-ph"),
            phDot: one<SVGCircleElement>(".ex-ph-dot"),
            digits: all<HTMLElement>(".ex-odo-strip"),
            jobs: JOBS.map((_, j) => {
                const head = one<HTMLElement>(`[data-ex-head="${j}"]`);
                const body = one<HTMLElement>(`[data-ex-body="${j}"]`);
                return {
                    fork: one<SVGPathElement>(`[data-ex-fork="${j}"]`),
                    lane: one<SVGPathElement>(`[data-ex-lane="${j}"]`),
                    merge: one<SVGPathElement>(`[data-ex-merge="${j}"]`),
                    head,
                    body,
                    shades: [head, body].map((el) => el.querySelector(".ex-shade") as HTMLElement),
                    items: all<HTMLElement>("li", body),
                };
            }),
        };
    }, []);

    useSceneRender(p, (v) => {
        const E = els.current;
        if (!E) return;
        const m = monthAt(v);
        const phY = yAt(m);
        const lastMerge = JOBS[JOBS.length - 1].merge;
        const end = Math.max(phY, lerp(yAt(SPAN), Y1, range(v, lastMerge[0], lastMerge[1])));

        E.prog.style.strokeDashoffset = String(Y1 - Y0 - (end - Y0));
        E.ticks.forEach((t, i) => t.classList.toggle("is-on", TICKS[i].y <= end + 0.5));
        E.ph.setAttribute("y1", String(phY));
        E.ph.setAttribute("y2", String(phY));
        E.phDot.setAttribute("cy", String(phY));

        const { month, year } = labelFor(m);
        const digits = String(month).padStart(2, "0") + String(year);
        E.digits.forEach((d, i) => (d.style.transform = `translateY(${-Number(digits[i]) * 32}px)`));

        JOBS.forEach((job, j) => {
            const J = E.jobs[j];
            J.fork.style.strokeDashoffset = String(1 - range(v, job.fork[0], job.fork[1]));
            J.lane.style.strokeDashoffset = String(1 - clamp((phY - job.ys) / (job.ye - job.ys)));
            J.merge.style.strokeDashoffset = String(1 - range(v, job.merge[0], job.merge[1]));

            // The header drops open from the lane; the body unfolds below it,
            // and folds back up into the header when the role ends.
            const ha = lerp(-90, 0, easeOut(range(v, job.head[0], job.head[1])));
            const fold = job.fold ? easeIn(range(v, job.fold[0], job.fold[1])) : 0;
            const ba = lerp(-90, 0, easeOut(range(v, job.body[0], job.body[1]))) - 90 * fold;

            box(J.head, CARD_X, job.headY, CARD_W, HEAD_H);
            J.head.style.transform = `perspective(900px) rotateX(${ha}deg)`;
            J.head.style.visibility = ha < -88 ? "hidden" : "visible";
            J.head.classList.toggle("is-stub", !!job.stub && range(v, job.stub[0], job.stub[1]) > 0.5);
            J.body.style.left = `${CARD_X}px`;
            J.body.style.top = `${job.headY + HEAD_H}px`;
            J.body.style.transform = `perspective(900px) rotateX(${ba}deg)`;
            J.body.style.visibility = ba < -88 ? "hidden" : "visible";
            J.shades[0].style.opacity = String((-ha / 90) * 0.7);
            J.shades[1].style.opacity = String((Math.min(-ba, 90) / 90) * 0.7);

            // Responsibilities appear as the playhead moves down the tenure.
            const n = J.items.length;
            J.items.forEach((li, i) => {
                const a = job.m0 + ((job.m1 - job.m0) * i) / n;
                const t = range(m, a, a + ((job.m1 - job.m0) * 0.7) / n);
                li.classList.toggle("is-on", t > 0);
                li.style.opacity = String(t);
                li.style.transform = `translateY(${6 * (1 - easeOut(t))}px)`;
            });
        });
    });

    return (
        <SceneCanvas
            tint="150, 220, 120"
            marker={{ index: 3, label: "Experience" }}
            width={W}
            height={H}
            navBand={NAV}
            pad={0}
            className="scene-canvas--phone"
        >
            <div ref={root} className="ex xp">
                <h1 className="mx-abs scene-title xp-title" style={{ transform: "translate(20px,100px)" }}>
                    Work <span>Experience</span>
                </h1>
                <p className="mx-abs scene-sub xp-sub" style={{ transform: "translate(20px,150px)" }}>
                    Two roles so far, from backend APIs to owning the full stack.
                </p>

                <div className="mx-abs ex-odo xp-odo" style={{ transform: "translate(370px,198px) translateX(-100%)" }} aria-hidden="true">
                    <span className="scene-label">Timeline</span>
                    <div className="ex-odo-digits mono">
                        {"00/0000".split("").map((ch, i) =>
                            ch === "/" ? (
                                <span key={i} className="ex-odo-sep">/</span>
                            ) : (
                                <span key={i} className="ex-odo-col">
                                    <span className="ex-odo-strip">
                                        {"0123456789".split("").map((d) => (
                                            <span key={d}>{d}</span>
                                        ))}
                                    </span>
                                </span>
                            )
                        )}
                    </div>
                </div>

                <svg className="ex-svg" viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H }} aria-hidden="true">
                    <path className="ex-axis" d={`M${AX_X} ${Y0} V${Y1}`} />
                    {TICKS.map((t, i) => (
                        <line key={i} className="ex-tick" x1={AX_X - (t.major ? 12 : 7)} x2={AX_X - 3} y1={t.y} y2={t.y} />
                    ))}
                    <path className="ex-prog" d={`M${AX_X} ${Y0} V${Y1}`} style={{ strokeDasharray: Y1 - Y0 }} />
                    {JOBS.map((job, j) => (
                        <g key={j}>
                            <path
                                className="ex-branch"
                                data-ex-fork={j}
                                pathLength={1}
                                d={`M${AX_X} ${job.ys - MH / 2} C${AX_X} ${job.ys - MH / 4} ${LANE_X} ${job.ys - MH / 4} ${LANE_X} ${job.ys}`}
                            />
                            <path className="ex-branch" data-ex-lane={j} pathLength={1} d={`M${LANE_X} ${job.ys} V${job.ye}`} />
                            <path
                                className="ex-branch"
                                data-ex-merge={j}
                                pathLength={1}
                                d={`M${LANE_X} ${job.ye} C${LANE_X} ${job.ye + MH / 4} ${AX_X} ${job.ye + MH / 4} ${AX_X} ${job.ye + MH / 2}`}
                            />
                        </g>
                    ))}
                    <line className="ex-ph" x1={AX_X - 14} x2={LANE_X + 10} />
                    <circle className="ex-ph-dot" cx={AX_X} r={4.5} />
                </svg>

                <span className="mx-abs ex-tl" style={{ transform: `translate(20px,${Y0 - 22}px)` }}>
                    {firstLabel}
                </span>
                {TICKS.filter((t) => t.year !== null).map((t) => (
                    <span key={t.y} className="mx-abs ex-tl xp-year" style={{ transform: `translate(0px,${t.y - 16}px)` }}>
                        {t.year}
                    </span>
                ))}
                <span className="mx-abs ex-tl" style={{ transform: `translate(20px,${Y1 + 10}px)` }}>
                    {lastLabel}
                </span>

                {EXPERIENCES.map((exp, j) => (
                    <div key={exp.company} className="ex-job" style={{ "--i": j } as CSSProperties}>
                        <div className="ex-body xp-body" data-ex-body={j} style={{ width: CARD_W }}>
                            <ul>
                                {exp.responsibilities.map((r) => (
                                    <li key={r}>{r}</li>
                                ))}
                            </ul>
                            <i className="ex-shade" />
                        </div>
                        <div className="ex-head xp-head" data-ex-head={j}>
                            <div className="ex-head-row">
                                <h3>{exp.title}</h3>
                                <span className="ex-focus mono">{exp.focus}</span>
                            </div>
                            <div className="ex-head-row ex-head-sub">
                                <span>{exp.company}</span>
                                <span className="ex-period mono">{exp.period}</span>
                            </div>
                            <i className="ex-shade" />
                        </div>
                    </div>
                ))}
            </div>
        </SceneCanvas>
    );
}
